import { UnitDetail, UnitTranslationPayload } from "@medapro-types/ConfigurationManager/components/unit-translation-config";
import api from "./api";
import endpoints from "./endpoints";
import { Guid } from "@/util/guid";
import { ProfileRule } from "@medapro-types/ConfigurationManager/ConfigurationManager";
import { MediaConverterPayload } from "@medapro-types/ConfigurationManager/components/media-converter-config";

export default {
  getUnitTranslations: async function (unitSystemId: Guid) {
    const response = await api.get(endpoints.configurationManager.unitTranslation.get, { params: { unitSystemId: unitSystemId } });
    return response.data;
  },
  getUnitSystems: async function () {
    const response = await api.get(endpoints.configurationManager.unitSystem.get);
    return response.data;
  },
  updateUnitSystems: async function (unitDetailsList: UnitDetail[]) {
    const response = await api.post(endpoints.configurationManager.unitSystem.update, unitDetailsList);
    return response.data;
  },
  renameUnitSystems: async function (unitSystemId: string, newName: string) {
    const response = await api.post(endpoints.configurationManager.unitSystem.rename, { params: { unitSystemId: unitSystemId, newName: newName } });
    return response.data;
  },
  deleteUnitSystems: async function (unitSystemIds: Guid[]) {
    const response = await api.post(endpoints.configurationManager.unitSystem.delete, unitSystemIds);
    return response.data;
  },
  deleteUnitTranslations: async function (unitTranslationIds: Guid[]) {
    const response = await api.post(endpoints.configurationManager.unitTranslation.delete, unitTranslationIds);
    return response.data;
  },
  copyUnitSystem: async function (sourceUnitSystemId: string) {
    const response = await api.post(endpoints.configurationManager.unitSystem.copy, {}, { params: { sourceUnitSystemId: sourceUnitSystemId } });
    return response.data;
  },
  updateUnitTranslations: async function (translationData: UnitTranslationPayload) {
    const response = await api.post(endpoints.configurationManager.unitTranslation.update, translationData);
    return response.data;
  },
  getRuleProfiles: async function (catalogMappingTargetId: string) {
    const response = await api.get(endpoints.configurationManager.ruleProfile.get, { params: { catalogMappingTargetId: catalogMappingTargetId } });
    return response.data;
  },
  createRuleProfile: async function (catalogMappingTargetId: string, name: string) {
    const response = await api.post(
      endpoints.configurationManager.ruleProfile.create,
      {},
      { params: { catalogMappingTargetId: catalogMappingTargetId, name: name } }
    );
    return response.data;
  },
  copyRuleProfile: async function (ruleProfileId: string) {
    const response = await api.post(endpoints.configurationManager.ruleProfile.copy, {}, { params: { ruleProfileId: ruleProfileId } });
    return response.data;
  },
  renameRuleProfile: async function (ruleProfileId: string, name: string) {
    const response = await api.post(endpoints.configurationManager.ruleProfile.rename, {}, { params: { ruleProfileId: ruleProfileId, name: name } });
    return response.data;
  },
  deleteRuleProfile: async function (ruleProfileId: string) {
    const response = await api.post(endpoints.configurationManager.ruleProfile.delete, {}, { params: { ruleProfileId: ruleProfileId } });
    return response.data;
  },
  updateXmlSchema: async function (ruleProfileId: string | undefined, file: File | null) {
    const response = await api.post(
      endpoints.configurationManager.ruleProfile.updateXmlSchema,
      { file },
      {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        params: { ruleProfileId: ruleProfileId }
      }
    );
    return response.data;
  },
  getRules: async function () {
    const response = await api.get(endpoints.configurationManager.ruleProfile.getRules);
    return response.data;
  },
  getProfileRule: async function (ruleProfileId: string) {
    const response = await api.get(endpoints.configurationManager.ruleProfile.getProfileRule, { params: { ruleProfileId: ruleProfileId } });
    return response.data;
  },
  deleteProfileRules: async function (ruleProfileRuleId: Guid[]) {
    const response = await api.post(endpoints.configurationManager.ruleProfile.deleteProfileRules, ruleProfileRuleId);
    return response.data;
  },
  updateProfileRules: async function (ruleProfileId: string, model: ProfileRule[]) {
    const response = await api.post(endpoints.configurationManager.ruleProfile.updateProfileRules, model, { params: { ruleProfileId: ruleProfileId } });
    return response.data;
  },
  getMediaConverterProfiles: async function () {
    const response = await api.get(endpoints.configurationManager.mediaConverterProfile.getProfiles);
    return response.data;
  },
  getMediaConverterProfileParameters: async function (profileId: string) {
    const response = await api.get(endpoints.configurationManager.mediaConverterProfile.getProfileParameters, { params: { profileId: profileId } });
    return response.data;
  },
  updateMediaConverterProfile: async function (mediaConverterProfileDetails: MediaConverterPayload) {
    const response = await api.post(endpoints.configurationManager.mediaConverterProfile.update, mediaConverterProfileDetails, {
      params: { language: mediaConverterProfileDetails.language }
    });
    return response.data;
  },
  renameMediaConverterProfile: async function (profileId: string, name: string) {
    const response = await api.post(endpoints.configurationManager.mediaConverterProfile.rename, {}, { params: { profileId: profileId, name: name } });
    return response.data;
  },
  copyMediaConverterProfile: async function (profileId: string, name: string) {
    const response = await api.post(endpoints.configurationManager.mediaConverterProfile.copy, {}, { params: { profileId, name } });
    return response.data;
  },
  deleteMediaConverterProfile: async function (profileId: string) {
    const response = await api.post(endpoints.configurationManager.mediaConverterProfile.delete, {}, { params: { profileId: profileId } });
    return response.data;
  },
  getSmartSnippetsEntries: async function (language: string) {
    const response = await api.get(endpoints.configurationManager.mediaConverterProfile.getSmartSnippetsEntries, { params: { language: language } });
    return response.data;
  },
  getAIConfiguration: async function () {
    const providerConfiguration = await api.get(endpoints.configurationManager.artificialIntelligence.providerConfiguration.get);
    const promptConfiguration = await api.get(endpoints.configurationManager.artificialIntelligence.promptConfiguration.get);
    return { providerConfiguration: providerConfiguration.data, promptConfiguration: promptConfiguration.data };
  },
  saveAIProviderConfiguration: async function (providerConfiguration: string) {
    const response = await api.post(endpoints.configurationManager.artificialIntelligence.providerConfiguration.update, providerConfiguration, {
      headers: { "Content-Type": "application/json" }
    });
    return response.data;
  },
  saveAIPromptConfiguration: async function (promptConfiguration: string) {
    const response = await api.post(endpoints.configurationManager.artificialIntelligence.promptConfiguration.update, promptConfiguration, {
      headers: { "Content-Type": "application/json" }
    });
    return response.data;
  }
};
